"use client";

import { FC } from "react";
import { cn } from "@/lib/utils";
import { ProgramStructure2Data } from "./_schema";
import Image from "next/image";

// Tools and Skills Section Component
export const ToolsAndSkillsSection: FC<{ parsedData: ProgramStructure2Data }> = ({ parsedData }) => (
    <div className={cn(
        "flex flex-col w-full self-start",
        "p-[10px] rounded-[10px] gap-[24px] w-full h-auto",
        "sm:p-[15px] sm:rounded-[7.5px] sm:gap-[59.25px] sm:w-[355px] sm:shrink-0",
        "md:p-[16px] md:rounded-[8px] md:gap-[60px] md:w-[408px] md:shrink-0",
        "lg:p-[20px] lg:rounded-[10px] lg:gap-[79px] lg:w-[474px] lg:shrink-0",
        "xl:p-[20px] xl:rounded-[10px] xl:gap-[79px] xl:w-[510px] xl:shrink-0"
    )}
    style={{
        backgroundColor: "rgba(255,255,255,0.1)"
    }}>
        {/* Tools Section */}
        <div className={cn(
            "flex flex-col w-full",
            "gap-[14px]",
            "sm:gap-[10.5px]",
            "md:gap-[12px]",
            "lg:gap-[14px]",
            "xl:gap-[14px]"
        )}>
            <p className={cn(
                "font-semibold leading-[1.49]",
                "text-[14px]",
                "sm:text-[12px]",
                "md:text-[14px]",
                "lg:text-[16px]",
                "xl:text-[16px]"
            )}
            style={{
                color: parsedData.tools_title_color || "#4cecfa",
                fontFamily: "var(--font-google-sans)"
            }}>
                {parsedData.tools_title}
            </p>
            <div className={cn(
                "flex flex-row flex-wrap w-full",
                "gap-x-[12px] gap-y-[12px]",
                "sm:gap-x-[9px] sm:gap-y-[12px]",
                "md:gap-x-[10.322px] md:gap-y-[12px]",
                "lg:gap-x-[12px] lg:gap-y-[12px]",
                "xl:gap-x-[12px] xl:gap-y-[12px]"
            )}>
                {parsedData.tools?.map((tool, index) => (
                    <div 
                        key={index}
                        className={cn(
                            "flex flex-col items-center justify-center border box-border",
                            "w-[80px] h-[44px] p-[10px] rounded-[20px]",
                            "sm:w-[101.25px] sm:h-[48px] sm:px-[15px] sm:py-[9.75px] sm:rounded-[15px]",
                            "md:w-[116.118px] md:h-[55.04px] md:px-[17.203px] md:py-[11.182px] md:rounded-[17.203px]",
                            "lg:w-[135px] lg:h-[64px] lg:px-[20px] lg:py-[13px] lg:rounded-[20px]",
                            "xl:w-[135px] xl:h-[64px] xl:px-[20px] xl:py-[13px] xl:rounded-[20px]"
                        )}
                        style={{
                            backgroundColor: tool.background_color || "rgba(255,255,255,0.1)",
                            borderColor: "rgba(255,255,255,0.2)"
                        }}
                    >
                        {tool.image && (
                            <div className={cn(
                                "relative shrink-0",
                                "w-[60px] h-[24px]",
                                "sm:w-[71.25px] sm:h-[28.5px]",
                                "md:w-[81.713px] md:h-[32.685px]",
                                "lg:w-[95px] lg:h-[38px]",
                                "xl:w-[95px] xl:h-[38px]"
                            )}>
                                <Image
                                    src={tool.image}
                                    alt={tool.alt_image || "Tool"}
                                    fill
                                    className="object-contain"
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>

        {/* Skills Section */}
        <div className={cn(
            "flex flex-col w-full",
            "gap-[14px]",
            "sm:gap-[10.5px]",
            "md:gap-[12px]",
            "lg:gap-[14px]",
            "xl:gap-[14px]"
        )}>
            <p className={cn(
                "font-semibold leading-[1.49]",
                "text-[14px]",
                "sm:text-[12px]",
                "md:text-[14px]",
                "lg:text-[16px]",
                "xl:text-[16px]"
            )}
            style={{
                color: parsedData.skills_title_color || "#4cecfa",
                fontFamily: "var(--font-google-sans)"
            }}>
                {parsedData.skills_title}
            </p>
            <div className={cn(
                "w-full flex flex-row flex-wrap",
                "gap-[13px]",
                "sm:gap-x-[9.75px] sm:gap-y-[13px]",
                "md:gap-[11.182px] md:gap-y-[13px]",
                "lg:gap-[13px] lg:gap-y-[13px]",
                "xl:gap-[13px] xl:gap-y-[13px]"
            )}>
                {parsedData.skills?.map((skill, index) => (
                    <div 
                        key={index}
                        className={cn(
                            "flex items-center justify-center border box-border line-clamp-1",
                            "h-[36px] px-[10px] py-[8px] rounded-[20px]",
                            "sm:h-[30px] sm:px-[7.5px] sm:py-[6px] sm:rounded-[15px]",
                            "md:h-[34px] md:px-[8.601px] md:py-[6.881px] md:rounded-[17.203px]",
                            "lg:h-[40px] lg:px-[10px] lg:py-[8px] lg:rounded-[20px]",
                            "xl:h-[40px] xl:px-[10px] xl:py-[8px] xl:rounded-[20px]"
                        )}
                        style={{
                            backgroundColor: skill.background_color || "rgba(255,255,255,0.1)",
                            borderColor: "rgba(255,255,255,0.2)"
                        }}
                    >
                        <p className={cn(
                            "font-semibold leading-[1.49] flex items-center gap-[4px]",
                            "text-[12px]",
                            "sm:text-[10.5px] sm:gap-[3px]",
                            "md:text-[12px] md:gap-[3.44px]",
                            "lg:text-[14px] lg:gap-[4px]",
                            "xl:text-[14px] xl:gap-[4px]"
                        )}
                        style={{
                            color: skill.text_color || "#FFFFFF",
                            fontFamily: "var(--font-google-sans)"
                        }}>
                            {/* Conditionally render icon or checkmark inline */}
                            {skill.icon ? (
                                <span className={cn(
                                    "relative inline-block shrink-0",
                                    "w-[14px] h-[14px]",
                                    "sm:w-[10.5px] sm:h-[10.5px]",
                                    "md:w-[12px] md:h-[12px]",
                                    "lg:w-[14px] lg:h-[14px]",
                                    "xl:w-[14px] xl:h-[14px]"
                                )}>
                                    <Image
                                        src={skill.icon}
                                        alt={skill.alt_icon || skill.name || "Skill icon"}
                                        fill
                                        className="object-contain"
                                    />
                                </span>
                            ) : (
                                <span>✓</span>
                            )}
                            {skill.name}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    </div>
);